import {
  apiActivityLanguages,
  apiActivityTypes,
  apiAges,
  apiAudiences,
  apiContacts,
  apiCountries,
  apiDenominations,
  apiEventPeople,
  apiFacilitatorMaterials,
  apiInternationalOrganizations,
  apiLanguages,
  apiLessons,
  apiLessonSelectionModes,
  apiMaterials,
  apiMyMaterials,
  apiOrganizations,
  apiOrganizationsExpertiseAreas,
  apiOrganizationsTypes,
  apiPeople,
  apiProfileListsData,
} from '@/utils/apiUrls';
import { LocalStorageKeys, SearchParams, SelectOptionsDataType } from '@/constants/common';
import {
  LessonModeOption,
  LessonsResponse,
  ProfileListsDataSelectOptionsResponse,
  SelectOptionsResponse,
} from '@/types/selectOptions';
import { Dispatch, SetStateAction } from 'react';
import { EventsActivityTypes, EventsEquippingLevels } from '@/constants/events';
import { Item } from '@/types/common';
import { useCommonFetchers } from '@/hooks/useCommonFetchers';
import { safeLocalStorage } from '@/utils/storage';

export type SimpleSelectOptionsDataType = Exclude<
  SelectOptionsDataType,
  SelectOptionsDataType.materials | SelectOptionsDataType.myMaterials
>;

export type FetchSelectOptionsParams = { setIsLoading: Dispatch<SetStateAction<boolean>> } & (
  | { dataType: SimpleSelectOptionsDataType; searchString?: string; eventId?: string; userId?: string }
  | {
      dataType: SelectOptionsDataType.materials;
      userId: string;
      eventType: EventsActivityTypes;
      equippingLevel?: EventsEquippingLevels;
      isReport?: boolean;
    }
  | { dataType: SelectOptionsDataType.materials; userId?: undefined; excludeMobileAudio?: boolean }
  | { dataType: SelectOptionsDataType.myMaterials; eventType: EventsActivityTypes }
);

export const useSelectsOptionsFetchers = () => {
  const { fetcher } = useCommonFetchers();

  const fetchSelectOptions = async (params: FetchSelectOptionsParams): Promise<SelectOptionsResponse> => {
    const { setIsLoading } = params;
    let apiPath;

    switch (params.dataType) {
      case SelectOptionsDataType.languages:
        apiPath = apiLanguages(params.searchString);
        break;
      case SelectOptionsDataType.activityLanguages:
        apiPath = apiActivityLanguages();
        break;
      case SelectOptionsDataType.organizations:
        apiPath = apiOrganizations('', { [SearchParams.filterSearch]: params.searchString });
        break;
      case SelectOptionsDataType.internationalOrganizations:
        apiPath = apiInternationalOrganizations(params.searchString);
        break;
      case SelectOptionsDataType.people:
        apiPath = params.eventId ? apiEventPeople(params.eventId, params.searchString) : apiPeople(params.searchString);
        break;
      case SelectOptionsDataType.materials:
        apiPath = params.userId
          ? apiFacilitatorMaterials(params.userId, params.eventType, params.equippingLevel, params.isReport)
          : apiMaterials('excludeMobileAudio' in params ? params.excludeMobileAudio : undefined);
        break;
      case SelectOptionsDataType.myMaterials:
        apiPath = apiMyMaterials(params.eventType);
        break;
      case SelectOptionsDataType.audiences:
        apiPath = apiAudiences();
        break;
      case SelectOptionsDataType.ages:
        apiPath = apiAges();
        break;
      case SelectOptionsDataType.activityTypes:
        apiPath = apiActivityTypes();
        break;
      case SelectOptionsDataType.countries:
        apiPath = apiCountries();
        break;
      case SelectOptionsDataType.denominations:
        apiPath = apiDenominations();
        break;
      case SelectOptionsDataType.organizationsExpertiseAreas:
        apiPath = apiOrganizationsExpertiseAreas();
        break;
      case SelectOptionsDataType.organizationsTypes:
        apiPath = apiOrganizationsTypes();
        break;
      case SelectOptionsDataType.contacts:
        apiPath = apiContacts(params.searchString);
        break;
      default:
        throw new Error('Invalid data type specified');
    }

    const response: SelectOptionsResponse = await fetcher(apiPath, setIsLoading);

    if (
      params.dataType === SelectOptionsDataType.organizations ||
      params.dataType === SelectOptionsDataType.languages
    ) {
      const cachedOptions = safeLocalStorage.getItem(LocalStorageKeys[params.dataType]);
      const newUniqueCachedOptions: Item[] = cachedOptions ? JSON.parse(cachedOptions) : [];

      response.data.forEach((item) => {
        if (!newUniqueCachedOptions.some((option) => option.value === item.value)) newUniqueCachedOptions.push(item);
      });

      safeLocalStorage.setItem(LocalStorageKeys[params.dataType], JSON.stringify(newUniqueCachedOptions));
    } else if (params.dataType === SelectOptionsDataType.contacts && params.userId) {
      return { ...response, data: response.data.filter((user) => user.value !== params.userId) };
    }

    return response;
  };

  const fetchProfileListsDataOptions = async (
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<ProfileListsDataSelectOptionsResponse> => fetcher(apiProfileListsData(), setIsLoading);

  const fetchLessons = async (
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    materialId: string,
  ): Promise<LessonsResponse> => fetcher(apiLessons(materialId), setIsLoading);

  const fetchLessonSelectionModes = async (
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    activityType: EventsActivityTypes,
  ): Promise<LessonModeOption[]> => fetcher(apiLessonSelectionModes(activityType), setIsLoading);

  return { fetchSelectOptions, fetchProfileListsDataOptions, fetchLessons, fetchLessonSelectionModes };
};
